import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Rocket, Menu, X, ChevronDown, Bot, Sparkles, Wrench, Trophy, TrendingUp, ArrowRight } from 'lucide-react';
import { BRAND } from '../data/siteData';

// True once the page has scrolled past the threshold
function useScrolled(threshold = 30) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return scrolled;
}

// Consolidated navigation IA. anchor items use '#', route items use '/'.
const MENU = [
  {
    id: 'missions',
    label: 'Missions & AI',
    primary: '#services',
    testId: 'nav-dropdown-missions',
    items: [
      { label: 'AI Systems & Workflows', target: '#ai', desc: 'Automation, chatbots & AI search visibility', icon: Bot, testId: 'nav-link-ai' },
      { label: 'All Missions', target: '#services', desc: 'Design, web, social, SEO & automation', icon: Rocket, testId: 'nav-link-services' },
      { label: 'Launch Sequence', target: '#process', desc: 'Our 4-step flight plan, discover to orbit', icon: Sparkles, testId: 'nav-link-process' },
      { label: 'Toolkit', target: '#tools', desc: 'The tech stack we fly with', icon: Wrench, testId: 'nav-link-tools' },
    ],
  },
  {
    id: 'work',
    label: 'Work',
    primary: '/work',
    testId: 'nav-dropdown-work',
    items: [
      { label: 'Case Studies', target: '/work', desc: 'Featured builds & full portfolio', icon: Trophy, testId: 'nav-link-work' },
      { label: 'Success Stories', target: '#success', desc: 'Client results, ROI & transformations', icon: TrendingUp, testId: 'nav-link-success' },
    ],
  },
  { id: 'pricing', label: 'Pricing', target: '/website-design-pricing', testId: 'nav-link-pricing' },
  { id: 'journal', label: 'Journal', target: '/journal', testId: 'nav-link-journal' },
  { id: 'contact', label: 'Contact', target: '#contact', testId: 'nav-link-contact' },
];

export default function Navbar() {
  const scrolled = useScrolled();
  const scrolledFar = useScrolled(500);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // smart-hide: hide navbar on scroll-down, reveal on scroll-up (mobile only via CSS)
  useEffect(() => {
    let last = window.scrollY;
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      if (y < 120) setHidden(false);
      else if (y > last + 6) setHidden(true);
      else if (y < last - 6) setHidden(false);
      last = y;
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // scroll-spy: track which homepage section is in view
  useEffect(() => {
    if (location.pathname !== '/') { setActiveSection(''); return; }
    const ids = ['ai', 'services', 'process', 'tools', 'success', 'contact'];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActiveSection(e.target.id); });
      },
      { rootMargin: '-40% 0px -40% 0px', threshold: 0 }
    );
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [location.pathname]);

  // which top-level item is active (route or in-view section)
  const activeId = (() => {
    const p = location.pathname;
    if (p.startsWith('/website-design-pricing')) return 'pricing';
    if (p.startsWith('/journal')) return 'journal';
    if (p.startsWith('/work')) return 'work';
    if (p === '/') {
      if (['ai', 'services', 'process', 'tools'].includes(activeSection)) return 'missions';
      if (activeSection === 'success') return 'work';
      if (activeSection === 'contact') return 'contact';
    }
    return '';
  })();

  // is a dropdown sub-item the current page/section?
  const isSubActive = (target) => {
    if (target.startsWith('#')) return location.pathname === '/' && activeSection === target.slice(1);
    return location.pathname.startsWith(target);
  };

  const go = (target) => {
    setMobileOpen(false);
    if (document.activeElement) document.activeElement.blur();
    if (target.startsWith('#')) {
      if (location.pathname !== '/') {
        navigate(`/${target}`);
      } else {
        document.querySelector(target)?.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(target);
      window.scrollTo({ top: 0 });
    }
  };

  const goHome = () => {
    setMobileOpen(false);
    if (location.pathname !== '/') navigate('/');
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <>
    <nav className={`boost-nav ${scrolled || mobileOpen ? 'scrolled' : ''} ${hidden && !mobileOpen ? 'nav-hidden' : ''}`} data-testid="navbar">
      <div className="boost-nav-inner">
        <button className="boost-logo" onClick={goHome} data-testid="nav-logo">
          <img src={BRAND.logo} alt="BOOST! Design & Marketing" />
        </button>

        <div className="boost-nav-links" data-testid="nav-desktop-links">
          {MENU.map((item) =>
            item.items ? (
              <div key={item.id} className="boost-nav-item">
                <button
                  className={`boost-nav-link boost-nav-trigger ${activeId === item.id ? 'active' : ''}`}
                  onClick={() => go(item.primary)}
                  aria-haspopup="menu"
                  data-testid={item.testId}
                >
                  {item.label}
                  <ChevronDown size={15} className="boost-nav-chev" />
                </button>
                <div className="boost-nav-dropdown" role="menu">
                  <div className="boost-nav-dropdown-grid">
                    {item.items.map((sub) => {
                      const Icon = sub.icon;
                      return (
                        <button
                          key={sub.testId}
                          className={`boost-nav-drop-item ${isSubActive(sub.target) ? 'active' : ''}`}
                          onClick={() => go(sub.target)}
                          data-testid={sub.testId}
                        >
                          <span className="boost-nav-drop-icon"><Icon size={18} /></span>
                          <span className="boost-nav-drop-text">
                            <span className="boost-nav-drop-label">{sub.label}<ArrowRight size={13} /></span>
                            <span className="boost-nav-drop-desc">{sub.desc}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : item.target.startsWith('/') ? (
              <Link key={item.id} to={item.target} className={`boost-nav-link ${activeId === item.id ? 'active' : ''}`} data-testid={item.testId}>
                {item.label}
              </Link>
            ) : (
              <button key={item.id} className={`boost-nav-link ${activeId === item.id ? 'active' : ''}`} onClick={() => go(item.target)} data-testid={item.testId}>
                {item.label}
              </button>
            )
          )}
          <button className="boost-btn boost-btn-primary boost-nav-cta" onClick={() => go('#contact')} data-testid="nav-cta">
            <Rocket size={16} />
            Start a Project
          </button>
        </div>

        <button className="boost-nav-burger" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu" data-testid="nav-burger">
          {mobileOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="boost-nav-mobile" data-testid="nav-mobile-menu">
          {MENU.map((item) =>
            item.items ? (
              <div className="boost-nav-mobile-group" key={item.id}>
                <span className="boost-nav-mobile-head">{item.label}</span>
                {item.items.map((sub) => {
                  const Icon = sub.icon;
                  return (
                    <button
                      key={sub.testId}
                      className={`boost-nav-mobile-link ${isSubActive(sub.target) ? 'active' : ''}`}
                      onClick={() => go(sub.target)}
                      data-testid={`mobile-${sub.testId}`}
                    >
                      <Icon size={17} /> {sub.label}
                    </button>
                  );
                })}
              </div>
            ) : item.target.startsWith('/') ? (
              <Link key={item.id} to={item.target} className={`boost-nav-mobile-link solo ${activeId === item.id ? 'active' : ''}`} data-testid={`mobile-${item.testId}`}>
                {item.label}
              </Link>
            ) : (
              <button key={item.id} className={`boost-nav-mobile-link solo ${activeId === item.id ? 'active' : ''}`} onClick={() => go(item.target)} data-testid={`mobile-${item.testId}`}>
                {item.label}
              </button>
            )
          )}
          <button className="boost-btn boost-btn-primary boost-nav-mobile-cta" onClick={() => go('#contact')} data-testid="mobile-nav-cta">
            <Rocket size={16} /> Start a Project
          </button>
        </div>
      )}
    </nav>

    <button
      className={`boost-sticky-cta boost-btn boost-btn-primary ${scrolledFar && !mobileOpen && activeSection !== 'contact' ? 'show' : ''}`}
      onClick={() => go('#contact')}
      aria-label="Start a project"
      data-testid="sticky-cta"
    >
      <Rocket size={17} />
      Start a Project
    </button>
    </>
  );
}
